import React from 'react'
import { Box, Stack, SvgIcon } from '@mui/material';
import { useSelection } from '../../hooks/use-selection';
import { CustomTable } from '../../components/custom-table';
import { CustomSearch } from '../../components/custom-search';
import { PageHeader } from "../../components/page-header";
import { vendorProductsHeadCells } from '../../seed/table-headers';
import EyeIcon from '@heroicons/react/24/outline/EyeIcon';
import { CREATE, ROWSPERPAGE, UPDATE, filterItems } from '../../utils/constant';
import { useDispatch, useSelector } from 'react-redux';
import PlusIcon from '@heroicons/react/24/outline/PlusIcon';
import TrashIcon from '@heroicons/react/24/outline/TrashIcon';
import PencilIcon from '@heroicons/react/24/outline/PencilIcon';
import { FormDialog } from '../../components/form-dialog';
import { vendorProductsFormFields } from '../../seed/form-fields';
import { postRequest } from '../../services/api-service';
import { createProductUrl, deleteProductUrl, getAllProductsUrl, updateProductUrl } from '../../seed/url';
import { CustomAlert } from '../../components/custom-alert';
import { ConfirmationDialog } from '../../components/confirmation-dialog';
import { useAuth } from '../../hooks/use-auth';

const useProductsIds = (specializations) => {
    return React.useMemo(
        () => {
            return specializations.map((customer) => customer.id);
        },
        [specializations]
    );
};

function VendorInventory() {
    const auth = useAuth();
    const dispatch = useDispatch();
    const productSideNav = useSelector((state) => state.ViewPaymentSideNavReducer);
    const [action, setAction] = React.useState(CREATE)
    const [rowsPerPage, setRowsPerPage] = React.useState(ROWSPERPAGE);
    const [products, setProducts] = React.useState({
        page: 1,
        total_results: 0,
        total_pages: 0,
        results: []
    });
    const [selectedData, setSelectedData] = React.useState({})
    const [searchTerm, setSearchTerm] = React.useState("")
    const [isLoading, setIsLoading] = React.useState(false)
    const [isDeleting, setIsDeleting] = React.useState(false)
    const productsIds = useProductsIds(products.results);
    const productsSelection = useSelection(productsIds);
    const [openCreateDialog, setOpenCreateDialog] = React.useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
    const [openAlert, setOpenAlert] = React.useState(false)
    const [severity, setSeverity] = React.useState("")
    const [severityMessage, setSeverityMessage] = React.useState("")
    const values = [
        {
            id: action === UPDATE ? selectedData.id : 0,
            product_name: action === UPDATE ? selectedData.product_name : "",
            description: action === UPDATE ? (selectedData.description || "") : "",
            product_vendor_id: auth?.user?.id,
            product_quantity: action === UPDATE ? selectedData.product_quantity : 0,
            product_amount: action === UPDATE ? selectedData.product_amount : 0,
            product_shipping_cost_in_dar: action === UPDATE ? selectedData.product_shipping_cost_in_dar : 0,
            product_shipping_cost_in_other_regions: action === UPDATE ? selectedData.product_shipping_cost_in_other_regions : 0,
            multiply_shipping_cost: action === UPDATE ? selectedData.multiply_shipping_cost : "",
        }
    ]
    const [order, setOrder] = React.useState('desc');
    const [orderBy, setOrderBy] = React.useState('id');

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const fetcher = React.useCallback(
        (page) => {
            setIsLoading(true)
            postRequest(
                getAllProductsUrl,
                {
                    query: searchTerm,
                    status: "ALL",
                    vendor_id: auth?.user?.id,
                    sort: orderBy + " " + order,
                    limit: rowsPerPage,
                    page: page
                },
                (data) => {
                    setProducts(data)
                    setIsLoading(false)
                },
                (error) => {
                    setProducts({
                        page: 1,
                        total_results: 0,
                        total_pages: 0,
                        results: [],
                    })
                    setIsLoading(false)
                },
            )
        },
        [rowsPerPage, searchTerm, orderBy, order, auth]
    );

    const handleSearch = (event) => {
        setSearchTerm(event.target.value)
    }

    React.useEffect(() => {
        fetcher(1)
    }, [fetcher])

    const handlePageChange = React.useCallback(
        (event, value) => {
            fetcher(value + 1)
        },
        [fetcher]
    );

    const handleRowsPerPageChange = React.useCallback(
        (event) => {
            setRowsPerPage(event.target.value);
        },
        []
    );

    const handleClickOpenCreateDialog = () => {
        setOpenCreateDialog(false)
        setSeverityMessage("Please Contact SMAI Express to Perform this Action")
        setSeverity("error")
        handleClickAlert()
    }

    const handleCloseCreateDialog = () => {
        action === UPDATE ? fetcher(products.page) : fetcher(1)
        setOpenCreateDialog(false)
        setAction(CREATE)
    }

    const handleClickOpenDeleteDialog = () => {
        setOpenDeleteDialog(false)
        setSeverityMessage("Please Contact SMAI Express to Perform this Action")
        setSeverity("error")
        handleClickAlert()
    }

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false)
    }

    const handleClickAlert = () => {
        setOpenAlert(true)
    }

    const handleCloseAlert = (event, reason) => {
        if (reason === 'clickaway') {
            return
        }

        setOpenAlert(false)
    }

    const handleDelete = async () => {
        postRequest(
            deleteProductUrl,
            {
                id: selectedData.id,
            },
            setIsDeleting,
            (data) => {
                fetcher(products.page)
                setSeverityMessage(data.message)
                setSeverity("success")
                handleClickAlert()
                setIsDeleting(false)
                handleCloseDeleteDialog()
            },
            (error) => {
                if (error?.response?.data?.message) {
                    setSeverityMessage(error.response.data.message[0])
                    setSeverity("error")
                    handleClickAlert()
                }
                setIsDeleting(false)
            },
        )
    }

    const onSelect = (data, openDialog) => {
        setSelectedData(data)
    }

    const productsPopoverItems = [
        {
            id: 'view',
            label: 'View',
            icon: <SvgIcon fontSize="small" sx={{ color: "text.primary" }}><EyeIcon /></SvgIcon>,
            onClick: () => {
                dispatch({
                    type: "TOOGLE_PAYMENT_SIDENAV",
                    payload: {
                        ...productSideNav,
                        openViewProductSideNav: true,
                        productSideNavContent: selectedData
                    },
                });
            },
        },
        {
            id: 'edit',
            label: 'Edit',
            icon: <SvgIcon fontSize="small" sx={{ color: "text.primary" }}><PencilIcon /></SvgIcon>,
            onClick: () => {
                setAction(UPDATE)
                handleClickOpenCreateDialog()
            },
        },
        {
            id: 'delete',
            label: 'Delete',
            icon: <SvgIcon fontSize="small" sx={{ color: "text.primary" }}><TrashIcon /></SvgIcon>,
            onClick: () => {
                handleClickOpenDeleteDialog()
            },
        },
    ]

    return (
        <>
            {openAlert &&
                <CustomAlert
                    openAlert={openAlert}
                    handleCloseAlert={handleCloseAlert}
                    severity={severity}
                    severityMessage={severityMessage}
                />
            }
            {openCreateDialog &&
                <FormDialog
                    open={openCreateDialog}
                    handleClose={handleCloseCreateDialog}
                    dialogTitle={"Product"}
                    action={action}
                    fields={vendorProductsFormFields}
                    values={values}
                    url={action === CREATE ? createProductUrl : updateProductUrl}
                />
            }
            {openDeleteDialog &&
                <ConfirmationDialog
                    open={openDeleteDialog}
                    handleClose={handleCloseDeleteDialog}
                    handleAction={handleDelete}
                    isPerformingAction={isDeleting}
                    dialogTitle={"Delete Alert"}
                    dialogContentText={"Are you sure you want to delete this item?"}
                />
            }
            <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
                <Stack spacing={2}>
                    <PageHeader
                      title="Products"
                      actions={[
                        {
                          label: "Add",
                          icon: <SvgIcon fontSize="small"><PlusIcon /></SvgIcon>,
                          onClick: handleClickOpenCreateDialog,
                        },
                      ]}
                    />
                    <CustomSearch
                        popoverItems={filterItems}
                        handleSearch={handleSearch}
                    />
                    <CustomTable
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        count={products.total_results}
                        items={products.results}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        onSelectOne={productsSelection.handleSelectOne}
                        onSelect={onSelect}
                        page={products.page >= 1 ? products.page - 1 : products.page}
                        rowsPerPage={rowsPerPage}
                        selected={productsSelection.selected}
                        headCells={vendorProductsHeadCells}
                        popoverItems={productsPopoverItems}
                        isLoading={isLoading}
                    />
                </Stack>
            </Box>
        </>
    )
}

export default VendorInventory